/**
 * The news crawl along the foot of the deck.
 *
 * He asked for "a footer Bloomberg style news for latest news" fed by the daily update Hermes
 * pushes. That is the right home for it: headlines are the one thing on this board he reads rather
 * than acts on, so they belong out of the working area entirely, and a crawl carries several of them
 * in the height of one line.
 *
 * WHAT MAKES A CRAWL DEFENSIBLE HERE, when scrolling text is usually a mistake. It is the only
 * element on the deck that is not competing for a decision: nothing below it needs clicking, no
 * count changes, and if he misses a headline it comes round again. That is the opposite of the
 * annunciator strip, which must never move.
 *
 * THREE RULES it has to obey to earn the space:
 *  - It pauses on hover, so a headline he wants to read stops instead of escaping.
 *  - It animates `transform` only, on a single element, so it costs one composited layer.
 *  - It does not run at all under `prefers-reduced-motion`, where it becomes a plain list.
 *
 * And it renders nothing when there is no news. An empty crawl loop would be a moving reminder that
 * a feature is not wired up.
 */

import type { JSX } from 'react';
import type { DashboardDailyNewsItem } from '@rbv/shared';
import { freshness } from './DailyBrief.js';

/**
 * Below this, the strip renders as a static row rather than a crawl.
 *
 * One or two headlines in a marquee looks broken: a long empty gap tracks past, then the same two
 * items again. A crawl needs enough content to read as continuous.
 */
const MIN_FOR_CRAWL = 3;

export function NewsTicker({
  items,
  at,
}: {
  items: DashboardDailyNewsItem[];
  /** When Hermes last pushed. Shown, because an old headline is a different claim from a new one. */
  at: string;
}): JSX.Element | null {
  if (items.length === 0) return null;
  const age = freshness(at);
  const crawl = items.length >= MIN_FOR_CRAWL;

  return (
    <aside className="crawl" data-crawl={crawl} aria-label="Latest news">
      <p className="crawl-label">
        News
        {/* The age sits in the label, not per headline: they all arrived in one push. */}
        <span className="crawl-age" data-stale={age.stale}>
          {age.label}
        </span>
      </p>

      <div className="crawl-window">
        {/*
          The track is duplicated when it crawls, and that duplication is what makes the loop
          seamless: the animation translates by exactly -50%, so the second copy is in the first
          copy's starting position at the moment it resets. One copy would visibly snap back.

          The duplicate is `aria-hidden`, or a screen reader would read every headline twice.
        */}
        <ul className="crawl-track">
          {items.map((n, i) => (
            <Headline key={`a-${i}-${n.title}`} item={n} />
          ))}
        </ul>
        {crawl && (
          <ul className="crawl-track" aria-hidden="true">
            {items.map((n, i) => (
              <Headline key={`b-${i}-${n.title}`} item={n} />
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}

function Headline({ item }: { item: DashboardDailyNewsItem }): JSX.Element {
  const body = (
    <>
      <span className="crawl-title">{item.title}</span>
      {item.source !== undefined && <span className="crawl-source">{item.source}</span>}
    </>
  );
  return (
    <li className="crawl-item">
      {item.url === undefined ? (
        body
      ) : (
        /*
         * New tab and `noopener`. These URLs came from outside via Hermes summarising mail from
         * anyone, and MO OS is an authenticated app: navigating its own tab to an attacker-chosen
         * page with a live `window.opener` is the one thing not to do. The scheme was already
         * checked server-side; this is the second layer.
         */
        <a className="crawl-link" href={item.url} target="_blank" rel="noopener noreferrer">
          {body}
        </a>
      )}
    </li>
  );
}
